import { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import 'dayjs/locale/es'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { MainLayout } from 'views/layouts/'
import {
  Container,
  ChartContainer,
  SubContainerChart,
  RowContainer,
  CardTitle,
  InfoUserContainer,
  ReportContainer,
  CardTable,
  TableBody,
  TableHead,
  NumberRow,
  TableList,
  HeadLeftCell,
  HeadRightCell,
  LeftCell,
  RightCell,
  CardTitleWithColor,
  CardInfoWithBorderTitle,
  Subtitle,
  WrapperQuadrantList,
  CountStressorsContainer,
  BlueText,
  WrapperInfo,
  InfoText,
  ScoreTitle,
  CardInfoWithBorder,
  RowTextWrapper,
  ScoreSectionWrapper,
  ScoreWrapperInfo,
  InfoHalfRow,
  RowCardsWrapper,
  BackButtonWrapper,
  HeaderButtonsWrapper,
  AdjustmentLevelText,
  ButtonContainer,
  DevelopmentButtonContainer,
} from './styled'
import { H2, H5, H4, P } from 'views/components/UI/Text'
import { StrokedButton, BackButton, Button } from 'views/components/UI/Buttons'
import CardDownloadResults from 'views/components/Cards/DownloadResults'
import BrainMetricsContainer from 'views/containers/BrainMetrics'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import { useNotification } from 'lib/context/notification.context'
import getBetesaResult, {
  getBetesaFollowUpSummaries,
  downloadBetesaComparisonPdf,
  downloadBetesaFollowUpsExcel,
} from 'services/results/betesa-results'
import sendReportToUser from 'services/reports/sendReportResultToUserEvaluated/sendReportToUser'
import { useNameLevelAlert } from 'hooks/useNameLevelAlert'
import { useNameQuadrant } from 'hooks/useNameQuadrant'
import { platformAdminRoutes, superAdminRoutes } from 'router/routes'
import { NO_INTERPRETABLE_TEXT } from 'constants/global.constants'
import {
  fetchUpdateUserTestAction,
  getLogoUrl,
  createBetesaRetake,
} from 'views/screens/Platform-administrator/constants/fetch_process'
import { sendTestsToUsers } from 'views/screens/Platform-administrator/services'
import { IdealProfileScores } from 'types/common'
import { fetchFeedBack } from 'views/screens/Evaluated-User/services/fetch-development-plan-betesa'
import { useTranslation } from 'react-i18next'
import { deleteDevelopmentPlan } from 'views/screens/super-admin/services/delete-development-plan'
import RollBackIcon from 'assets/icons/RollBackIcon'
import getBetesaReport from 'services/pdfReports/getBetesaReport'
import { closeReportWindow, createPendingReportWindow, openReportInWindow } from 'utils/openReportInNewTab'

const ResultsBetesa = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const location: any = useLocation()
  const { getError, getSuccess, getWarning } = useNotification()
  const { token } = useSelector((state: any) => state.user)
  const { role } = useSelector((state: any) => state.auth)
  dayjs.extend(utc)
  const testId = location.state?.testId
  const processId = location.state.processId ? location.state.processId : location.state.process_id
  const userId = location.state?.userId
  const userName = location.state?.userName
  const companyId = location.state?.companyId
  const betesaResultId = location.state?.betesaResultId
  const requestedUserTestId = Number(location.state?.userTestId) || undefined

  const [testData, setTestData] = useState<any>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingReport, setLoadingReport] = useState<boolean>(false)
  const [participation, setParticipation] = useState<boolean>(false)
  const [isSendingReport, setIsSendingReport] = useState<boolean>(false)
  const [disabledButton, setDisabledButton] = useState<boolean>(false)
  const [disablePlanButton, setDisablePlanButton] = useState<boolean>(false)
  const [betesaHistory, setBetesaHistory] = useState<any[]>([])
  const [comparisonIds, setComparisonIds] = useState<[number | null, number | null]>([null, null])

  const userTestId = testData?.test_info?.user_test_id || requestedUserTestId || ''
  const processName = testData?.test_info?.process_name ? testData?.test_info?.process_name : ''
  const comparisonResults = comparisonIds
    .filter((id): id is number => typeof id === 'number')
    .map((id) => betesaHistory.find((item) => Number(item.user_test_id) === id))
    .filter(Boolean)
  const graphResult = comparisonResults[0]
  const comparisonResult = comparisonResults[1]
  const hasLaterMeasurement = betesaHistory.some(
    (measurement) => Number(measurement.user_test_id) > Number(userTestId)
  )

  const formatMeasurementLabel = (measurement: any) => {
    const type = measurement.is_follow_up
      ? `Follow Up${measurement.follow_up_ordinal ? ` ${measurement.follow_up_ordinal}` : ''}`
      : measurement.is_retake ? 'Nueva oportunidad' : 'Medición original'
    const date = measurement.answer_date ? dayjs.utc(measurement.answer_date).format('DD/MM/YY') : null
    return date ? `${type} · ${date}` : type
  }

  const getSelectedComparisonIds = () => comparisonIds.filter((id): id is number => typeof id === 'number')

  const downloadComparisonFile = async (type: 'pdf' | 'excel') => {
    const ids = getSelectedComparisonIds()
    if (ids.length !== 2 || ids[0] === ids[1]) {
      getWarning('Selecciona dos mediciones diferentes para descargar el comparativo')
      return
    }

    try {
      const buffer =
        type === 'pdf'
          ? await downloadBetesaComparisonPdf(userId, processId, ids)
          : await downloadBetesaFollowUpsExcel(userId, processId, ids)
      const blob = new Blob([buffer], {
        type: type === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = type === 'pdf' ? 'Comparativo_Follow-Up_Betesa.pdf' : 'Follow-Ups_Betesa.xlsx'
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      getError('No fue posible generar el reporte comparativo')
    }
  }

  useEffect(() => {
    if (!userTestId) return

    fetchFeedBack(userTestId)
      .then((res: any) => {
        if (res.data.status === 'OK') {
          setDisablePlanButton(true)
        }
      })
      .catch(() => {
        setDisablePlanButton(false)
      })
  }, [userTestId])

  const getIdealProfile = useCallback((): IdealProfileScores | undefined => {
    const profileValues = testData?.ideal_profile?.profile_values
    if (!profileValues || profileValues[0]?.min <= 0) return

    const idealProfileData: IdealProfileScores = ['FI', 'FD', 'BI', 'BD'].reduce((acc: any, quadrant) => {
      acc[quadrant] = profileValues.find((item: any) => item.quadrant === quadrant)?.min || 0
      return acc
    }, {} as IdealProfileScores)

    if (idealProfileData.FI) {
      return idealProfileData
    }
  }, [testData])

  const getResults = async () => {
    try {
      const response = await getBetesaResult(testId, userId, requestedUserTestId)
      if (response.status === 'OK') {
        if (typeof response.data === 'string') {
          getError('El usuario no ha contestado la prueba betesa')
          setLoading(false)
        } else {
          setTestData(response.data)
          setLoading(false)
        }
      } else {
        getError('Error al obtener los resultados')
        setLoading(false)
      }
    } catch (error) {
      getError('Error al obtener los resultados de betesa')
    }
  }

  useEffect(() => {
    if (token && !loading) {
      setLoading(true)
      getResults()
    }
    //  eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  useEffect(() => {
    if (!userId || !processId) return

    getBetesaFollowUpSummaries(userId, processId).then((response) => {
      if (response.status === 'OK' && Array.isArray(response.data)) {
        setBetesaHistory(response.data)
        const currentId = Number(userTestId)
        const previous = response.data.find((item: any) => Number(item.user_test_id) !== currentId)
        if (response.data.length > 1) {
          setComparisonIds([
            currentId,
            previous ? Number(previous.user_test_id) : Number(response.data[0].user_test_id),
          ])
        }
      }
    })
  }, [processId, userId, userTestId])

  const handleSendReport = () => {
    if (isSendingReport) return
    setIsSendingReport(true)
    sendReportToUser({
      userId,
      companyId,
      userTestId: testData?.test_info.user_test_id,
    })
      .then((res) => {
        if (res.status === 'OK' || res.status === 'success') {
          setParticipation(true)
          fetchUpdateUserTestAction(testData?.test_info.user_test_id, true)
          getSuccess('Reporte enviado correctamente')
        } else {
          getError('Error al enviar el reporte: ' + res.message)
          setIsSendingReport(false)
        }
      })
      .finally(() => {
        setIsSendingReport(false)
      })
      .catch((error) => {
        getError('Error al intentar enviar el reporte: ' + error)
        setIsSendingReport(false)
      })
  }

  const handleResendTest = async () => {
    try {
      setLoading(true)
      const logoUrl = await getLogoUrl(processId)
      const retake = await createBetesaRetake(testData?.test_info.user_test_id, companyId)
      if (retake.status !== 'OK' || !retake.data?.user_test_id) {
        getError(retake.message || 'No fue posible crear la nueva prueba Betesa')
        return
      }

      const response = await sendTestsToUsers({
        process_id: processId,
        users_data: [{ user_id: userId, user_test_id: retake.data.user_test_id }],
        company_id: companyId,
        type: 'test_resend',
        count: 1,
        email_template: {
          logo: logoUrl?.cobranding?.logo,
          date: dayjs.utc().add(7, 'day').format('DD/MM/YYYY'),
        },
      })

      if (response.status !== 'OK' || !Array.isArray(response.users) || !response.users.some((user: { send_mail?: boolean }) => user.send_mail)) {
        const needsReview = Array.isArray(response.users) && response.users.some(
          (user: { delivery_status?: string }) =>
            user.delivery_status === 'pending' || user.delivery_status === 'review',
        )
        getWarning(needsReview
          ? 'La nueva prueba quedó registrada sin consumo de créditos. El envío requiere revisión; no lo reintentes hasta confirmar si salió el correo.'
          : 'No se pudo confirmar el envío de la nueva prueba. Revisa su estado antes de intentarlo de nuevo.')
        return
      }

      getSuccess('Nueva prueba Betesa enviada sin consumo de créditos')
      setDisabledButton(true)
    } catch (error) {
      console.error('Error al reenviar la prueba:', error)
      const message = (error as { response?: { data?: { message?: string } } })
        .response?.data?.message
      getError(message || 'No se pudo completar la nueva prueba Betesa. Puedes reintentar sin generar un cobro duplicado.')
    } finally {
      setLoading(false)
    }
  }

  const parsedLevel = useNameLevelAlert(testData?.score_detail?.alert_level)
  const parsedQuadrant = useNameQuadrant(testData?.test_info?.domination)

  const isUninterpretable = parsedQuadrant === NO_INTERPRETABLE_TEXT
  const handleDisabledClick = () => {
    getWarning('El usuario tiene configurada la descarga de reporte automático o ya se autorizó el acceso')
  }

  const handlePlanDisabledClick = () => {
    getWarning(t('global.notifications.planAlreadyCreated'))
  }

  const handleCreatePlan = () => {
    const body = {
      companyId,
      processId,
      userId,
      nameProcess: processName,
      testData: [{ testId, typeTest: 'Betesa Premium', typeLabel: 'betesa', userTestId, domination: 'Interpretable' }],
    }
    navigate(platformAdminRoutes.DevelopmentPlan, {
      state: {
        body,
        userName,
        flag: true,
      },
    })
  }

  const handleDeletePlan = async () => {
    const response = await deleteDevelopmentPlan(userTestId)
    if (response.status === 'OK') {
      getSuccess('Plan de desarrollo restaurado correctamente')
      setDisablePlanButton(false)
    } else {
      getError('Error al restaurar el plan de desarrollo')
    }
  }

  const downloadReport = async () => {
    const reportWindow = createPendingReportWindow()

    try {
      setLoadingReport(true)
      const response = await getBetesaReport(userTestId)

      const { url } = response

      await openReportInWindow(reportWindow, url)

      getSuccess(t('global.notifications.reportGenerated'))
    } catch (error) {
      closeReportWindow(reportWindow)
      console.error('Error al descargar el reporte de Betesa:', error)
      getError('No fue posible abrir el reporte de Betesa.')
    } finally {
      setLoadingReport(false)
    }
  }

  return (
    <MainLayout>
      {!loading && testData ? (
        <Container>
          <RowContainer justifyContent="space-between">
            <H2>Betesa - {userName || ''}</H2>
            <HeaderButtonsWrapper>
              {role === 'platform_admin' || role === 'super_platform_admin' ? (
                <StrokedButton
                  disabled={disablePlanButton || isUninterpretable}
                  onClick={handleCreatePlan}
                  onDisabledClick={handlePlanDisabledClick}
                >
                  Crear plan de desarrollo
                </StrokedButton>
              ) : null}
              {role === 'platform_admin' || role === 'super_platform_admin' ? null : (
                <Link to={`${superAdminRoutes.ReinterpretationBetesa}?id=${betesaResultId}`}>
                  <StrokedButton onClick={() => {}}>Interpretar</StrokedButton>
                </Link>
              )}

              <StrokedButton
                onClick={handleSendReport}
                disabled={
                  testData?.test_info?.participant_report ||
                  participation ||
                  testData?.test_info?.access_report ||
                  isUninterpretable
                }
                onDisabledClick={handleDisabledClick}
              >
                Autorizar acceso a usuario
              </StrokedButton>

              {(role === 'platform_admin' || role === 'super_platform_admin') && isUninterpretable && !hasLaterMeasurement ? (
                <Button onClick={handleResendTest} disabled={disabledButton}>
                  Reenviar prueba
                </Button>
              ) : null}
            </HeaderButtonsWrapper>
          </RowContainer>
          <RowContainer>
            <WrapperInfo>
              <H4>Datos de la Prueba</H4>
              <RowContainer>
                <Subtitle>Empresa:</Subtitle>
                <InfoText>{testData?.test_info?.company_name}</InfoText>

                <Subtitle>Nombre del Proceso:</Subtitle>
                <InfoText>{testData?.test_info?.process_name}</InfoText>

                <Subtitle>Fecha de realización:</Subtitle>
                <InfoText>
                  {testData?.test_info?.answer_date
                    ? dayjs.utc(testData.test_info.answer_date).format('DD/MM/YY')
                    : 'Sin respuesta'}
                </InfoText>
              </RowContainer>
            </WrapperInfo>
          </RowContainer>
          <InfoUserContainer>
            <ScoreSectionWrapper>
              <ScoreWrapperInfo>
                <ScoreTitle>Puntaje de pruebas</ScoreTitle>
                <CardInfoWithBorder>
                  <RowTextWrapper>
                    <Subtitle>Cuadrante dominante:</Subtitle> <InfoText>{t(parsedQuadrant)}</InfoText>
                  </RowTextWrapper>
                  <RowTextWrapper>
                    <Subtitle>Puntajes por cuadrante:</Subtitle>
                    {!isUninterpretable ? (
                      <WrapperQuadrantList>
                        <li>FI: {testData?.score_detail?.development_scores?.FI_raw_score}</li>
                        <li>BI: {testData?.score_detail?.development_scores?.BI_raw_score}</li>
                        <li>BD: {testData?.score_detail?.development_scores?.BD_raw_score}</li>
                        <li>FD: {testData?.score_detail?.development_scores?.FD_raw_score}</li>
                      </WrapperQuadrantList>
                    ) : (
                      <P margin="12px 0px">{NO_INTERPRETABLE_TEXT}</P>
                    )}
                  </RowTextWrapper>
                  <RowTextWrapper>
                    <Subtitle>Nivel de alerta:</Subtitle>{' '}
                    <InfoText>{!isUninterpretable ? t(parsedLevel) : NO_INTERPRETABLE_TEXT}</InfoText>
                  </RowTextWrapper>
                  <RowTextWrapper>
                    <Subtitle>Nivel de ajuste:</Subtitle>{' '}
                    <AdjustmentLevelText>
                      {!isUninterpretable ? (
                        <>
                          {testData?.adjustment_level} {testData?.adjustment_level_score ? '-' : ''}{' '}
                          {testData?.adjustment_level_score}
                          {testData?.adjustment_level_score ? '%' : ''}
                        </>
                      ) : (
                        <>{NO_INTERPRETABLE_TEXT}</>
                      )}
                    </AdjustmentLevelText>
                  </RowTextWrapper>
                  <RowTextWrapper>
                    <Subtitle>Nivel:</Subtitle> <InfoText>{testData?.test_info?.level}</InfoText>
                  </RowTextWrapper>
                </CardInfoWithBorder>
              </ScoreWrapperInfo>
              <ButtonContainer>
                <ReportContainer>
                  {!isUninterpretable ? (
                    <CardDownloadResults
                      title="Betesa"
                      type="betesa"
                      onClick={downloadReport}
                      user_id={userId}
                      test_id={testId}
                      user_test_id={userTestId}
                      processId={processId}
                      planToDownload={disablePlanButton}
                      userName={userName}
                      setLoadingReport={setLoadingReport}
                    />
                  ) : null}
                </ReportContainer>
                {disablePlanButton && (role === 'super_admin' || role === 'special_admin') && (
                  <DevelopmentButtonContainer onClick={handleDeletePlan}>
                    <StrokedButton
                      onClick={handleDeletePlan}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px' }}
                    >
                      <RollBackIcon color="#2d749c" />
                      Restaurar plan de desarrollo
                    </StrokedButton>
                  </DevelopmentButtonContainer>
                )}
              </ButtonContainer>
            </ScoreSectionWrapper>
            {!isUninterpretable ? (
              <ChartContainer>
                <SubContainerChart style={{ transform: 'scale(0.85)' }}>
                  <BrainMetricsContainer
                    showPolarArea
                    result={{
                      FI: graphResult ? Number(graphResult.FI) : testData?.score_detail?.development_scores?.FI_raw_score,
                      BI: graphResult ? Number(graphResult.BI) : testData?.score_detail?.development_scores?.BI_raw_score,
                      BD: graphResult ? Number(graphResult.BD) : testData?.score_detail?.development_scores?.BD_raw_score,
                      FD: graphResult ? Number(graphResult.FD) : testData?.score_detail?.development_scores?.FD_raw_score,
                    }}
                    childhood={testData?.childhood_and_youth}
                    idealProfile={getIdealProfile()}
                    dominantQuadrant={testData?.score_detail?.thought_structure}
                    comparisonResult={
                      comparisonResult
                        ? {
                            FI: Number(comparisonResult.FI),
                            BI: Number(comparisonResult.BI),
                            BD: Number(comparisonResult.BD),
                            FD: Number(comparisonResult.FD),
                          }
                        : undefined
                    }
                    resultLabel={graphResult ? formatMeasurementLabel(graphResult) : 'Medición original'}
                    comparisonResultLabel={comparisonResult ? formatMeasurementLabel(comparisonResult) : 'Follow Up'}
                  />
                </SubContainerChart>
                <SubContainerChart></SubContainerChart>
              </ChartContainer>
            ) : null}
            {betesaHistory.length > 1 && (
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginTop: '12px' }}>
                <strong>Comparar mediciones:</strong>
                {([0, 1] as const).map((index) => (
                  <select
                    key={index}
                    value={comparisonIds[index] ?? ''}
                    onChange={(event) => {
                      const nextIds: [number | null, number | null] = [...comparisonIds]
                      nextIds[index] = event.target.value ? Number(event.target.value) : null
                      setComparisonIds(nextIds)
                    }}
                    style={{ padding: '8px', border: '1px solid #C2C2C2', borderRadius: '6px' }}
                  >
                    <option value="">Seleccionar medición</option>
                    {betesaHistory.map((measurement) => (
                      <option key={measurement.user_test_id} value={measurement.user_test_id}>
                        {formatMeasurementLabel(measurement)}
                      </option>
                    ))}
                  </select>
                ))}
                <StrokedButton onClick={() => downloadComparisonFile('pdf')}>Descargar PDF</StrokedButton>
                <StrokedButton onClick={() => downloadComparisonFile('excel')}>Descargar Excel</StrokedButton>
              </div>
            )}
            <RowCardsWrapper>
              <InfoHalfRow>
                <CardInfoWithBorderTitle>
                  <CardTitleWithColor>
                    <H5>Estresores</H5>
                  </CardTitleWithColor>
                  <TableBody>
                    <CountStressorsContainer>
                      {testData.stressors_score?.length ? (
                        testData.stressors_score.map((item: any) => (
                          <BlueText key={item}> {!isUninterpretable ? `${item.score}/100` : 'N/A'} </BlueText>
                        ))
                      ) : (
                        <BlueText>N/A</BlueText>
                      )}
                    </CountStressorsContainer>
                  </TableBody>
                  <div style={{ height: '70%', padding: 20 }}>
                    {!testData?.stressors?.length && <P>No se encontraron estresores</P>}
                    {isUninterpretable ? <>N/A</> : testData?.stressors?.map((item: any) => <P key={item}>{item}</P>)}
                  </div>
                </CardInfoWithBorderTitle>
              </InfoHalfRow>
              <InfoHalfRow>
                <CardInfoWithBorderTitle>
                  <CardTitle>
                    <H5 margin="10px 0px">
                      Estado emocional: {!isUninterpretable ? testData?.emotional_state?.text : 'N/A'}
                    </H5>
                  </CardTitle>
                  <CardTable>
                    <TableHead style={{ background: '#E6EEF3', borderColor: '#E6EEF3' }}>
                      <tr>
                        <th style={{ borderColor: '#E6EEF3' }}>Adjetivos positivos</th>
                        <th style={{ borderColor: '#E6EEF3' }}>Adjetivos negativos</th>
                      </tr>
                    </TableHead>
                    <TableBody>
                      <NumberRow>
                        <HeadLeftCell>
                          {!isUninterpretable ? testData?.emotional_state?.positive_adjectives?.score : 'N/A'}
                        </HeadLeftCell>
                        <HeadRightCell>
                          {!isUninterpretable ? testData?.emotional_state?.negative_adjectives?.score : 'N/A'}
                        </HeadRightCell>
                      </NumberRow>
                      <tr>
                        <LeftCell>
                          {!isUninterpretable ? (
                            <TableList style={{ listStyle: 'none' }}>
                              {testData?.emotional_state?.positive_adjectives?.adjectives?.map((item: any) => (
                                <li key={item}>{item}</li>
                              ))}
                            </TableList>
                          ) : (
                            <P>N/A</P>
                          )}
                        </LeftCell>
                        <RightCell>
                          {!isUninterpretable ? (
                            <TableList style={{ listStyle: 'none' }}>
                              {testData?.emotional_state?.negative_adjectives?.adjectives?.map((item: any) => (
                                <li key={item}>{item}</li>
                              ))}
                            </TableList>
                          ) : (
                            <P>N/A</P>
                          )}
                        </RightCell>
                      </tr>
                    </TableBody>
                  </CardTable>
                </CardInfoWithBorderTitle>
              </InfoHalfRow>
            </RowCardsWrapper>
          </InfoUserContainer>
        </Container>
      ) : (
        <ModalLoader isLoading={loading} />
      )}
      <BackButtonWrapper>
        <BackButton onClick={() => navigate(-1)} />
      </BackButtonWrapper>
      <ModalLoader isLoading={loadingReport} fontWeight={600} margin="20px 0" text="Estamos generando el archivo..." />
    </MainLayout>
  )
}

export default ResultsBetesa
